// Direct import of selector module needed instead of useBrand hook in middleware context where hooks cannot be used.
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook
import urlParamsMapper from 'components/data/URLParams/URLParamsMapper'
import {MiddlewareType} from 'middlewares'
import {
  CLEAR_REFRESHED_STATUS,
  HISTORY_BACK,
  NAVIGATE_TO_SEARCH,
  REFRESH_PAGE,
  REFRESH_PAGE_ONCE,
  SCROLL_TO,
  SCROLL_TO_WINDOW_TOP,
  setPpLoaded
} from 'modules/browser/actions'
import Router from 'next/router'
import {mergeDeepRight} from 'ramda'
import {initForter} from 'services/forter'
import {
  getPageAsRefreshedStatus,
  setPageAsRefreshedStatus
} from 'services/sessionStorage'
import {getSearchResultsUrl} from 'utils/urls'

import {getBrand} from '@vio/core/src/_web/brand/modules/selectors'
import {
  scrollToElementOnCenter,
  scrollToWindowTop
} from '@vio/core/src/_web/utils/browser'
import {navigateTo} from '@vio/core/src/_web/utils/browser/navigateTo'
import {SIGN_UP_TOKEN} from '@vio/core/src/offer/business/privateDeals'
import {buildUrl} from '@vio/core/src/utils/url'

const buildSearchParams = ({
  placeID,
  isAuthenticated,
  isAudience,
  params
}: {
  placeID?: string
  isAuthenticated?: boolean
  isAudience?: boolean
  params?: {
    checkIn?: string
    checkOut?: string
    rooms?: string
    optimizeRooms?: string
    utm_source?: string
    utm_campaign?: string
    // bookingHint=1 (show date confirmation tooltip) is used to open the search box for mobile and date confirmation tooltip on desktop
    // it was added as part of this task: https://app.shortcut.com/findhotel/story/154347/update-landing-experience-for-users-with-no-dates-default-dates-widget-traffic
    bookingHint?: string
  }
}) => {
  // eslint-disable-next-line  new-cap
  const {searchWebsiteParams, metaParams} = urlParamsMapper()
  const authenticated = isAuthenticated ? 1 : undefined
  const token = isAudience ? SIGN_UP_TOKEN : undefined
  const {gsite, vf} = metaParams

  const searchWebsiteParamsToBeMerged = {
    ...searchWebsiteParams,
    ...(placeID ? {placeId: placeID, hotelId: undefined} : {}),
    ...params
  }

  return mergeDeepRight(searchWebsiteParamsToBeMerged, {
    authenticated,
    token,
    gsite,
    vf
  })
}

/* eslint-disable complexity */
const middleware: MiddlewareType = store => next => async action => {
  const {getState} = store

  next(action)

  switch (action.type) {
    case HISTORY_BACK: {
      Router.back()
      break
    }

    case REFRESH_PAGE: {
      Router.reload()
      break
    }

    case REFRESH_PAGE_ONCE: {
      if (!getPageAsRefreshedStatus()) {
        setPageAsRefreshedStatus(true)
        window.setTimeout(() => {
          Router.reload()
        }, 1000)
      }

      break
    }

    case CLEAR_REFRESHED_STATUS: {
      setPageAsRefreshedStatus(false)
      break
    }

    case SCROLL_TO: {
      scrollToElementOnCenter(action.elementRef)
      break
    }

    case SCROLL_TO_WINDOW_TOP: {
      scrollToWindowTop()
      break
    }

    case NAVIGATE_TO_SEARCH: {
      const {
        isAuthenticated,
        isAudience,
        isNewTab,
        checkIn,
        checkOut,
        rooms,
        optimizeRooms,
        utm_source,
        utm_campaign,
        placeID,
        bookingHint
      } = action.params

      const searchParams = {
        forceCurrencyChange: 1,
        forceLanguageChange: 1,
        ...buildSearchParams({
          placeID,
          isAudience,
          isAuthenticated,
          params: {
            checkIn,
            checkOut,
            rooms,
            optimizeRooms,
            utm_source,
            utm_campaign,
            bookingHint
          }
        })
      }
      const brand = getBrand(getState())

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      isNewTab
        ? window.open(
            buildUrl(getSearchResultsUrl(brand), searchParams),
            '_blank'
          )
        : navigateTo(buildUrl(getSearchResultsUrl(brand), searchParams))

      break
    }

    case setPpLoaded.type: {
      initForter()

      break
    }

    default: {
      break
    }
  }
}

export default middleware
